import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { connect, iAmReady } from '@tiendanube/nexo';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import HomePage from './components/HomePage';
import Login from './components/Login';
import PortalPerfil from './components/PortalPerfil';
import OnboardingPage from './components/OnboardingPage';
import OnboardingTiendanube from './components/OnboardingTiendanube';
import ConectarTienda from './components/ConectarTienda';
import PortalDashboard from './components/PortalDashboard';
import PortalPedidos from './components/PortalPedidos';
import PortalPedidoDetalle from './components/PortalPedidoDetalle';
import PortalColectas from './components/PortalColectas';
import PortalCotizador from './components/PortalCotizador';
import PortalSoporte from './components/PortalSoporte';
import Setup from './components/Setup';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AdminDashboard from './components/AdminDashboard';
import AdminTarifas from './components/AdminTarifas';
import AdminContratos from './components/AdminContratos';
import AdminTiendanube from './components/AdminTiendanube';
import AdminPedidos from './components/AdminPedidos';
import PagoResult from './components/PagoResult';
import StatusPage from './components/StatusPage';
import { isAdminRole } from './lib/roles';
import nexoClient from './nexoClient';
import './App.css';
import './components/Portal.css';

function RequireAdmin({ children }) {
  const { user, isAuthenticated, authReady } = useAuth();

  if (!authReady) {
    return (
      <div className="page-shell">
        <section className="panel loading-panel">
          <p className="eyebrow">Verificando permisos</p>
          <h2>Validando acceso al admin...</h2>
          <p>Comprobando el rol real contra la sesion activa.</p>
        </section>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return isAdminRole(user?.rol) ? children : <Navigate to="/portal" replace />;
}

function RequirePortal({ children }) {
  const { user, isAuthenticated, authReady } = useAuth();

  if (!authReady) {
    return (
      <div className="page-shell">
        <section className="panel loading-panel">
          <p className="eyebrow">Verificando acceso</p>
          <h2>Cargando portal...</h2>
          <p>Comprobando la sesion activa.</p>
        </section>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isAdminRole(user?.rol)) {
    return <Navigate to="/admin" replace />;
  }

  return children;
}

function AppRoutes() {
  const location = useLocation();
  // El onboarding se muestra como un flujo aislado. Evitar montar la navbar
  // mientras AuthProvider termina de validar la sesión también mantiene
  // estable el árbol que React debe reconciliar al avanzar de paso.
  const showNavbar = location.pathname !== '/' && location.pathname !== '/setup';

  return (
    <>
      {showNavbar ? <Navbar /> : null}
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/onboarding-tiendanube" element={<OnboardingTiendanube />} />
          <Route path="/conectar-tienda" element={<ConectarTienda />} />
          <Route path="/setup" element={<Setup />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/estado" element={<StatusPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route
            path="/perfil"
            element={
              <RequirePortal>
                <PortalPerfil />
              </RequirePortal>
            }
          />
          <Route
            path="/portal"
            element={
              <RequirePortal>
                <PortalDashboard />
              </RequirePortal>
            }
          />
          <Route
            path="/pedidos"
            element={
              <RequirePortal>
                <PortalPedidos />
              </RequirePortal>
            }
          />
          <Route
            path="/pedidos/:id"
            element={
              <RequirePortal>
                <PortalPedidoDetalle />
              </RequirePortal>
            }
          />
          <Route
            path="/colectas"
            element={
              <RequirePortal>
                <PortalColectas />
              </RequirePortal>
            }
          />
          <Route
            path="/colectas/nueva"
            element={
              <RequirePortal>
                <PortalColectas />
              </RequirePortal>
            }
          />
          <Route
            path="/colectas/:id/editar"
            element={
              <RequirePortal>
                <PortalColectas />
              </RequirePortal>
            }
          />
          <Route
            path="/colectas/:id"
            element={
              <RequirePortal>
                <PortalColectas />
              </RequirePortal>
            }
          />
          <Route
            path="/cotizar"
            element={
              <RequirePortal>
                <PortalCotizador />
              </RequirePortal>
            }
          />
          <Route
            path="/soporte"
            element={
              <RequirePortal>
                <PortalSoporte />
              </RequirePortal>
            }
          />
          <Route path="/mi-cuenta" element={<Navigate to="/portal" replace />} />
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminDashboard />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/tarifas"
            element={
              <RequireAdmin>
                <AdminTarifas />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/reglas"
            element={
              <RequireAdmin>
                <AdminContratos />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/tiendanube"
            element={
              <RequireAdmin>
                <AdminTiendanube />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/tiendanube/pedidos"
            element={
              <RequireAdmin>
                <AdminPedidos />
              </RequireAdmin>
            }
          />
          <Route
            path="/admin/contratos"
            element={
              <RequireAdmin>
                <Navigate to="/admin/reglas" replace />
              </RequireAdmin>
            }
          />
          <Route path="/pago/exitoso" element={<PagoResult />} />
          <Route path="/pago/fallido" element={<PagoResult />} />
          <Route path="/pago/pendiente" element={<PagoResult />} />
        </Routes>
      </main>
    </>
  );
}

function NexoGate({ children }) {
  const [nexoReady, setNexoReady] = useState(false);
  const [nexoError, setNexoError] = useState('');
  const initiatedRef = useRef(false);

  useEffect(() => {
    if (initiatedRef.current) {
      return;
    }

    // Fuera del iframe de Tiendanube no es necesario conectar Nexo.
    if (window.self === window.top) {
      setNexoReady(true);
      return;
    }

    initiatedRef.current = true;

    const iniciarNexo = async () => {
      try {
        await connect(nexoClient);
        console.log('Nexo conectado correctamente');
        iAmReady(nexoClient);
        console.log('iAmReady enviado a Tiendanube');
        setNexoReady(true);
      } catch (error) {
        console.error('Error iniciando Nexo:', error);
        // No bloqueamos la app si Nexo falla; asi se puede diagnosticar en consola.
        setNexoError(error?.message || String(error));
        setNexoReady(true);
      }
    };

    iniciarNexo();
  }, []);

  if (!nexoReady) {
    return (
      <div className="page-shell">
        <section className="panel loading-panel">
          <p className="eyebrow">Conectando</p>
          <h2>Conectando con Tiendanube...</h2>
          <p>Esperando la confirmacion de Nexo.</p>
        </section>
      </div>
    );
  }

  return (
    <>
      {nexoError ? (
        <div className="page-shell" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, padding: '0.75rem' }}>
          <div className="alert error" role="alert">
            <strong>Advertencia de Nexo:</strong> {nexoError}
          </div>
        </div>
      ) : null}
      {children}
    </>
  );
}

function App() {
  return (
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <AuthProvider>
        <NexoGate>
          <AppRoutes />
        </NexoGate>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
